import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  MessageSquare,
  Plus,
  ChevronDown,
  ChevronUp,
  Clock,
  Trash2,
  Loader2,
} from "lucide-react";
import { formatDistanceToNow, isToday, format } from "date-fns";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

interface Session {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

interface SessionSelectorProps {
  currentSessionId: string | null;
  onSelectSession: (sessionId: string | null) => void;
  projectId: string;
  disabled?: boolean;
  refreshTrigger?: number;
}

export function SessionSelector({
  currentSessionId,
  onSelectSession,
  projectId,
  disabled = false,
  refreshTrigger = 0,
}: SessionSelectorProps) {
  const { getIdToken } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  // Fetch sessions from backend
  const fetchSessions = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get Firebase auth token
      const token = await getIdToken();

      // Use the NEW Claude session endpoint
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const response = await axios.get(`${apiUrl}/api/claude-sessions`, {
        params: { projectId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSessions(response.data.sessions);
      } else {
        setError("Failed to load sessions");
      }
    } catch (err: any) {
      console.error("Failed to fetch sessions:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to load sessions"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch sessions when dropdown opens OR when there's a current session on mount
  useEffect(() => {
    if (isOpen && projectId) {
      fetchSessions();
    }
  }, [isOpen, projectId]);

  // Fetch sessions on mount if there's a current session (to display title)
  useEffect(() => {
    if (currentSessionId && projectId && sessions.length === 0) {
      fetchSessions();
    }
  }, [currentSessionId, projectId]);

  // Refetch sessions when refreshTrigger changes (e.g., title updated)
  useEffect(() => {
    if (refreshTrigger > 0 && projectId) {
      fetchSessions();
    }
  }, [refreshTrigger]);

  // Delete a session
  const handleDeleteSession = async (
    id: string,
    title: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent dropdown from closing
    setSessionToDelete({ id, title });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!sessionToDelete) return;

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const response = await axios.delete(
        `${apiUrl}/api/claude-sessions/${sessionToDelete.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh sessions list
        fetchSessions();

        // If deleting current session, switch to new session
        if (sessionToDelete.id === currentSessionId) {
          onSelectSession(null);
        }
      }
    } catch (err: any) {
      console.error("Failed to delete session:", err);
      setError("Failed to delete session");
    } finally {
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    }
  };

  // Format session display text
  const getSessionDisplayText = (session: Session | null) => {
    if (!session) {
      return "New Chat";
    }

    // Use title or fallback
    const title = session.title || "Untitled Session";
    const truncated =
      title.length > 40 ? title.substring(0, 40) + "..." : title;
    return truncated;
  };

  // Get current session from list
  const currentSession = sessions.find((s) => s.id === currentSessionId);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          className="w-full flex items-center justify-between h-8 px-2.5 rounded-md text-left font-normal border border-border/60 hover:border-border hover:bg-accent/50 transition-colors outline-none disabled:opacity-50"
        >
          <div className="flex items-center gap-2 truncate">
            {currentSessionId ? (
              <>
                <MessageSquare className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="truncate text-sm">
                  {getSessionDisplayText(currentSession || null)}
                </span>
              </>
            ) : (
              <>
                <Plus className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-sm">New Chat</span>
              </>
            )}
          </div>
          {isOpen ? (
            <ChevronUp className="w-3 h-3 text-muted-foreground flex-shrink-0 ml-1" />
          ) : (
            <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0 ml-1" />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="w-80 max-h-96 overflow-y-auto border-border/30"
      >
        {/* New Session Option */}
        <DropdownMenuItem
          onClick={() => onSelectSession(null)}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Plus className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium">New Chat</span>
        </DropdownMenuItem>

        {sessions.length > 0 && <DropdownMenuSeparator />}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading sessions...
            </span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-sm text-destructive px-3 py-2">{error}</div>
        )}

        {/* Sessions List */}
        {!loading &&
          !error &&
          sessions.map((session) => (
            <DropdownMenuItem
              key={session.id}
              onClick={() => onSelectSession(session.id)}
              className={`
              flex flex-col gap-1 py-2 cursor-pointer
              ${session.id === currentSessionId ? "bg-accent" : ""}
            `}
            >
              <div className="flex items-start justify-between w-full">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm font-medium truncate">
                      {session.title || "Untitled Session"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 ml-6">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {(() => {
                        const sessionDate = new Date(session.updatedAt);
                        const now = new Date();
                        // Handle future timestamps (clock drift/timezone issues)
                        if (sessionDate > now) {
                          return "just now";
                        }
                        // For today's sessions, show actual time for precision
                        if (isToday(sessionDate)) {
                          return `Today at ${format(sessionDate, "h:mm a")}`;
                        }
                        return formatDistanceToNow(sessionDate, {
                          addSuffix: true,
                        });
                      })()}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) =>
                    handleDeleteSession(
                      session.id,
                      session.title || "Untitled Session",
                      e
                    )
                  }
                  className="p-1 h-auto hover:bg-destructive/10"
                >
                  <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            </DropdownMenuItem>
          ))}

        {/* Empty State */}
        {!loading && !error && sessions.length === 0 && (
          <div className="text-sm text-muted-foreground px-3 py-4 text-center">
            No previous chats found
          </div>
        )}
      </DropdownMenuContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px] w-full max-w-[90vw] rounded-lg">
          <DialogHeader>
            <DialogTitle>Delete Chat Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this chat session? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DropdownMenu>
  );
}
